// Admin access-control surface copy — the `/admin/access` users table. The
// status/level enum labels live in access.ts (shared with the pending page);
// this file carries the table chrome: head, columns, controls, grant editor.

import type { MsgSection } from "./types.js";

export const admin: MsgSection = {
  // --- Page shell ---
  "admin.access.title": { en: "Access control", de: "Zugriffsverwaltung" },
  "admin.access.subtitle": {
    en: "Approve or block accounts and grant per-project access levels.",
    de: "Konten freigeben oder sperren und projektbezogene Zugriffsstufen vergeben.",
  },
  "admin.access.back": { en: "Back", de: "Zurück" },

  // --- States ---
  "admin.access.loading": { en: "Loading users…", de: "Nutzer werden geladen…" },
  "admin.access.denied.title": { en: "No access", de: "Kein Zugriff" },
  "admin.access.denied.body": {
    en: "Your account isn't an administrator. Ask an existing administrator to grant you admin rights.",
    de: "Dein Konto ist keine Administratorin. Bitte eine bestehende Administratorin, dir Adminrechte zu erteilen.",
  },
  "admin.access.error.title": {
    en: "Couldn't load users",
    de: "Nutzer konnten nicht geladen werden",
  },
  "admin.access.empty": { en: "No users yet.", de: "Noch keine Nutzer." },

  // --- Users table ---
  "admin.access.col.user": { en: "User", de: "Nutzer" },
  "admin.access.col.status": { en: "Status", de: "Status" },
  "admin.access.col.grants": { en: "Project access", de: "Projektzugriff" },
  "admin.access.badge.admin": { en: "Admin", de: "Admin" },

  // --- Status control ---
  "admin.access.status.label": { en: "Set status", de: "Status setzen" },

  // --- Grants ---
  "admin.access.grants.none": {
    en: "No project grants.",
    de: "Keine Projektfreigaben.",
  },
  "admin.access.grants.remove": { en: "Remove", de: "Entfernen" },
  "admin.access.grants.expand": { en: "Show project access", de: "Projektzugriff anzeigen" },
  "admin.access.grants.collapse": { en: "Hide project access", de: "Projektzugriff verbergen" },

  // --- Add-grant editor ---
  "admin.access.grants.add": { en: "Add grant", de: "Freigabe hinzufügen" },
  "admin.access.grants.project": { en: "Project", de: "Projekt" },
  "admin.access.grants.projectPlaceholder": {
    en: "Search projects…",
    de: "Projekte suchen…",
  },
  "admin.access.grants.level": { en: "Level", de: "Stufe" },
  "admin.access.grants.save": { en: "Grant", de: "Freigeben" },
  "admin.access.grants.cancel": { en: "Cancel", de: "Abbrechen" },
  "admin.access.grants.noProjects": {
    en: "No matching projects.",
    de: "Keine passenden Projekte.",
  },

  // --- Mutation error ---
  "admin.access.saveError": {
    en: "Update failed: {error}",
    de: "Aktualisierung fehlgeschlagen: {error}",
  },

  // --- Admin hub (/admin) ---
  "admin.home.navLabel": { en: "Admin", de: "Admin" },
  "admin.home.title": { en: "Admin", de: "Admin" },
  "admin.home.subtitle": {
    en: "Operator tools for access control, the project map, analytics, and Anthropic keys.",
    de: "Werkzeuge für Zugriffsverwaltung, Projekt-Karte, Analytics und Anthropic-Schlüssel.",
  },
  "admin.home.back": { en: "Back", de: "Zurück" },
  "admin.home.link.access": { en: "Access control", de: "Zugriffsverwaltung" },
  "admin.home.link.accessDesc": {
    en: "Approve or block accounts and grant per-project access levels.",
    de: "Konten freigeben oder sperren und projektbezogene Zugriffsstufen vergeben.",
  },
  "admin.home.link.codeMap": { en: "Projekt-Karte", de: "Projekt-Karte" },
  "admin.home.link.codeMapDesc": {
    en: "Generate or refresh a project's code map.",
    de: "Projekt-Karte eines Projekts generieren oder aktualisieren.",
  },
  "admin.home.link.analytics": { en: "Analytics", de: "Analytics" },
  "admin.home.link.analyticsDesc": {
    en: "Usage, spend, and session drill-downs.",
    de: "Nutzung, Kosten und Session-Details.",
  },
  "admin.home.link.keys": { en: "Anthropic keys", de: "Anthropic-Schlüssel" },
  "admin.home.link.keysDesc": {
    en: "Assign keys to users and projects.",
    de: "Schlüssel an Nutzer und Projekte zuweisen.",
  },

  // --- Code map admin (/admin/code-map) ---
  "admin.codeMap.title": { en: "Projekt-Karte", de: "Projekt-Karte" },
  "admin.codeMap.subtitle": {
    en: "Generate or refresh the code map used to ground the assistant in a project's structure.",
    de: "Die Projekt-Karte generieren oder aktualisieren, die dem Assistenten die Projektstruktur vermittelt.",
  },
  "admin.codeMap.back": { en: "Back", de: "Zurück" },
  "admin.codeMap.loading": { en: "Loading status…", de: "Status wird geladen…" },
  "admin.codeMap.denied.title": { en: "No access", de: "Kein Zugriff" },
  "admin.codeMap.denied.body": {
    en: "Your account isn't an administrator. Ask an existing administrator to grant you admin rights.",
    de: "Dein Konto ist keine Administratorin. Bitte eine bestehende Administratorin, dir Adminrechte zu erteilen.",
  },
  "admin.codeMap.error": {
    en: "Couldn't load status: {error}",
    de: "Status konnte nicht geladen werden: {error}",
  },
  "admin.codeMap.generateError": {
    en: "Generation failed: {error}",
    de: "Generierung fehlgeschlagen: {error}",
  },
  "admin.codeMap.noMap": {
    en: "No code map has been generated for this project yet.",
    de: "Für dieses Projekt wurde noch keine Projekt-Karte generiert.",
  },
  "admin.codeMap.generate": { en: "Generate", de: "Generieren" },
  "admin.codeMap.regenerate": { en: "Regenerate", de: "Neu generieren" },
  "admin.codeMap.generating": { en: "Generating…", de: "Wird generiert…" },
  "admin.codeMap.fact.version": { en: "Version", de: "Version" },
  "admin.codeMap.fact.nodeCount": { en: "Nodes", de: "Knoten" },
  "admin.codeMap.fact.generatedAt": { en: "Generated", de: "Generiert am" },
  "admin.codeMap.fact.sha": { en: "Branch SHA", de: "Branch-SHA" },
  "admin.codeMap.droppedPaths": {
    en: "{count} proposed code paths didn't match the repo and were dropped.",
    de: "{count} vorgeschlagene Code-Pfade passten nicht zum Repository und wurden verworfen.",
  },
  "admin.codeMap.truncated": {
    en: "Only {count} of {total} files were used, so areas that live in the rest won't be offered as choices.",
    de: "Es wurden nur {count} von {total} Dateien verwendet; Bereiche in den übrigen Dateien werden nicht zur Auswahl angeboten.",
  },
  "admin.codeMap.scannedFiles": {
    en: "Built from all {count} files in the repo.",
    de: "Aus allen {count} Dateien des Repositories erstellt.",
  },
  // Shown while the request is in flight. Generation is a repo walk plus an
  // LLM call and runs ~40s; without a stated expectation the disabled button
  // reads as a hang and invites a reload.
  "admin.codeMap.generatingHint": {
    en: "This usually takes about a minute. Leaving this page cancels nothing — the map is saved when it finishes.",
    de: "Das dauert normalerweise etwa eine Minute. Ein Verlassen der Seite bricht nichts ab — die Karte wird gespeichert, sobald sie fertig ist.",
  },
  "admin.codeMap.generatingElapsed": {
    en: "{seconds}s elapsed",
    de: "{seconds}s vergangen",
  },
  // Prepended to the raw upstream reason for the one failure an operator hits
  // by accident: picking a project whose repo is empty or unreachable.
  "admin.codeMap.noRepo": {
    en: "This project has no repository Loom can read.",
    de: "Dieses Projekt hat kein Repository, das Loom lesen kann.",
  },

  // --- Anthropic keys (/admin/keys) ---
  "admin.keys.title": { en: "Anthropic keys", de: "Anthropic-Schlüssel" },
  "admin.keys.loading": { en: "Loading keys…", de: "Schlüssel werden geladen…" },
  "admin.keys.loadFailed": {
    en: "Couldn't load the keys.",
    de: "Die Schlüssel konnten nicht geladen werden.",
  },
  "admin.keys.off": {
    en: "Assigning keys isn't available here.",
    de: "Schlüssel zuweisen ist hier nicht verfügbar.",
  },
  "admin.keys.empty": { en: "No keys saved.", de: "Keine Schlüssel gespeichert." },
  "admin.keys.col.owner": { en: "Owner", de: "Inhaber" },
  "admin.keys.col.keyId": { en: "Key id", de: "Schlüssel-ID" },
  "admin.keys.col.spend": { en: "Last 30 days", de: "Letzte 30 Tage" },
  "admin.keys.owner.user": { en: "User {id}", de: "Nutzer {id}" },
  "admin.keys.owner.project": { en: "Project {id}", de: "Projekt {id}" },
  // A row under a uuid: a key of another org, which no call runs on.
  "admin.keys.notInUse": { en: "Not in use", de: "Nicht in Gebrauch" },
  "admin.keys.removeOwner": {
    en: "Remove the key of {owner}",
    de: "Schlüssel von {owner} entfernen",
  },
  "admin.keys.unassignConfirm": {
    en: "Remove the key of {owner}? It is switched off at Anthropic and stops working everywhere.",
    de: "Schlüssel von {owner} entfernen? Er wird bei Anthropic abgeschaltet und funktioniert nirgends mehr.",
  },
  "admin.keys.assign.title": { en: "Assign a key", de: "Schlüssel zuweisen" },
  "admin.keys.kind.user": { en: "User", de: "Nutzer" },
  "admin.keys.kind.project": { en: "Project", de: "Projekt" },
  "admin.keys.assign.id": { en: "Id", de: "ID" },
  "admin.keys.assign.submit": { en: "Assign", de: "Zuweisen" },
  "admin.keys.error.ownerId": {
    en: "Enter a user id or a project id.",
    de: "Gib eine Nutzer-ID oder eine Projekt-ID ein.",
  },
  "admin.keys.error.reserved": {
    en: "This is one of Loom's own keys.",
    de: "Das ist einer der eigenen Schlüssel von Loom.",
  },
  "admin.keys.error.notInOrg": {
    en: "This key isn't from Loom's Anthropic organization.",
    de: "Dieser Schlüssel gehört nicht zur Anthropic-Organisation von Loom.",
  },
  "admin.keys.error.ambiguous": {
    en: "More than one key in Loom's Anthropic organization matches this one.",
    de: "Mehr als ein Schlüssel der Anthropic-Organisation von Loom passt zu diesem.",
  },
  "admin.keys.error.notAssignable": {
    en: "Only keys whose name starts with {prefix} can be assigned.",
    de: "Nur Schlüssel, deren Name mit {prefix} beginnt, können zugewiesen werden.",
  },
  "admin.keys.error.noWorkspace": {
    en: "This key belongs to no workspace. Use a key from a workspace.",
    de: "Dieser Schlüssel gehört zu keinem Workspace. Nimm einen Schlüssel aus einem Workspace.",
  },
  "admin.keys.error.taken": {
    en: "This key is already assigned elsewhere.",
    de: "Dieser Schlüssel ist schon anderswo zugewiesen.",
  },
  "admin.keys.error.assigned": {
    en: "This owner already has an assigned key. Remove it first.",
    de: "Hier ist schon ein Schlüssel zugewiesen. Entferne ihn zuerst.",
  },
  "admin.keys.error.reviewSession": {
    en: "No key can be assigned to the review sign-in's user.",
    de: "Dem Nutzer der Review-Anmeldung kann kein Schlüssel zugewiesen werden.",
  },
  "admin.keys.error.deactivateFailed": {
    en: "Anthropic didn't switch the key off, so it stays assigned. Try again.",
    de: "Anthropic hat den Schlüssel nicht abgeschaltet, er bleibt zugewiesen. Versuch es nochmals.",
  },
  "admin.keys.assignFailed": {
    en: "Couldn't assign the key. Try again.",
    de: "Der Schlüssel konnte nicht zugewiesen werden. Versuch es nochmals.",
  },
  "admin.keys.removeFailed": {
    en: "Couldn't remove the key. Try again.",
    de: "Der Schlüssel konnte nicht entfernt werden. Versuch es nochmals.",
  },
};
